import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { IconSend } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';

export function ReportsPage() {
  const { reportName } = useParams<{ reportName?: string }>();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingPdf, setSendingPdf] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [showWhatsAppForm, setShowWhatsAppForm] = useState<boolean>(false);
  const [whatsappMessage, setWhatsappMessage] = useState<string>('');

  useEffect(() => {
    const fetchIframeUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const reportEndpoint = reportName === 'spar-mobile-expo' 
          ? '/reports/spar-mobile-expo/iframe-url'
          : '/reports/spar-mobile-expo/iframe-url';

        const response = await api.get(reportEndpoint);
        setIframeUrl(response.data.iframeUrl);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 
          'Failed to load report. Please check your Metabase configuration.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchIframeUrl();
  }, [reportName]);

  const handleSendPdfViaWhatsApp = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setSendingPdf(true);
    setError(null);

    try {
      const response = await api.post('/reports/spar-mobile-expo/send-pdf-via-whatsapp', {
        phoneNumber: phoneNumber.trim(),
        caption: whatsappMessage || 'SPAR Mobile Expo Report',
      });

      if (response.data.success) {
        toast.success('PDF sent successfully via WhatsApp!');
        setShowWhatsAppForm(false);
        setPhoneNumber('');
        setWhatsappMessage('');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        'Failed to send PDF via WhatsApp. Please check your configuration.'
      );
    } finally {
      setSendingPdf(false);
    }
  };

  if (loading) {
    return <PageLoader message="Loading report..." />;
  }

  if (error && !iframeUrl) {
    return (
      <div>
        <PageHeader
          title="Reports"
          subtitle="SPAR Mobile Expo Report"
        />
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Error loading report</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="SPAR Mobile Expo Report"
        action={
          <Button
            variant="default"
            onClick={() => setShowWhatsAppForm(!showWhatsAppForm)}
          >
            <IconSend className="size-4" />
            Send PDF via WhatsApp
          </Button>
        }
      />

      {showWhatsAppForm && (
        <MainCard className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Send PDF via WhatsApp</h3>
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number (with country code)</Label>
              <Input
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+27123456789"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsappMessage">Message (optional)</Label>
              <Textarea
                id="whatsappMessage"
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                placeholder="SPAR Mobile Expo Report"
                rows={3}
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-4">
              <Button
                variant="default"
                onClick={handleSendPdfViaWhatsApp}
                disabled={sendingPdf || !phoneNumber.trim()}
              >
                {sendingPdf ? 'Sending...' : 'Send PDF'}
              </Button>
              <Button
                onClick={() => {
                  setShowWhatsAppForm(false);
                  setPhoneNumber('');
                  setWhatsappMessage('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </MainCard>
      )}

      {iframeUrl && (
        <MainCard className="p-0 overflow-hidden" contentClassName="!p-0" style={{ height: 'calc(100vh - 280px)' }}>
          <iframe
            src={iframeUrl}
            frameBorder={0}
            width="100%"
            height="100%"
            title="SPAR Mobile Expo Report"
            className="block border-none"
          />
        </MainCard>
      )}
    </div>
  );
}
